import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  GridSize,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { addMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'features/locations/api/getLocation';
import WeatherChart from 'features/weatherWalls/WeatherChart';
import { useEffect, useState } from 'react';
import { ForecastDto } from 'types';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import ForecastFullscreenPopup from './ForecastFullscreenPopup';
import { SelectedChart } from './types';

interface Props {
  open: boolean;
  handleClose: () => void;
  forecast: ForecastDto | undefined;
  lastUpdated: string;
}

const ForecastDetails = ({
  open,
  handleClose,
  forecast,
  lastUpdated,
}: Props) => {
  const { data: location } = useLocation({
    locationId: forecast?.locationId ? forecast.locationId : -1,
  });
  const { monthAbbreviatedWordDayFormatWithYear, timezone } = useUserDateTime();
  const [chartWidth, setChartWidth] = useState<GridSize>(6);
  const [fullScreenChart, setFullScreenChart] = useState<SelectedChart>('none');
  // Resize the drawer with the rest of the component
  useEffect(() => {
    const handleResize = () => {
      if (!window) return;
      if (window.innerWidth > 1000) setChartWidth(4);
      else setChartWidth(6);
    };
    handleResize();
    if (window) window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <Dialog
      sx={{ zIndex: 9999999999 }}
      fullWidth
      maxWidth='xl'
      open={open}
      onBackdropClick={handleClose}
    >
      <DialogTitle>Forecast for {location?.name}</DialogTitle>
      <DialogContent>
        {forecast && (
          <Grid container>
            <>
              <Grid item container wrap='nowrap'>
                {forecast.dailyForecast.map((day) => (
                  <Grid
                    key={day.date}
                    container
                    item
                    xs
                    wrap='nowrap'
                    direction='column'
                  >
                    <Grid item container xs justifyContent='center'>
                      <Typography>
                        {formatInTimeZone(
                          addMinutes(
                            new Date(day.date),
                            new Date(day.date).getTimezoneOffset()
                          ),
                          timezone,
                          monthAbbreviatedWordDayFormatWithYear
                        )}
                      </Typography>
                    </Grid>
                    <Grid item container xs justifyContent='center'>
                      <Box>
                        <img
                          height='65px'
                          width='65px'
                          src={day.icon ? day.icon : ''}
                          alt='Forecast icon'
                        />
                      </Box>
                    </Grid>
                    <Grid item container direction='column' alignItems='center'>
                      <Grid item xs>
                        <Typography variant='body2'>{day.condition}</Typography>
                      </Grid>
                      <Grid container item xs justifyContent='center'>
                        <Typography
                          variant='body2'
                          sx={{ pl: '.2rem', pr: '.2rem' }}
                        >
                          {day.maxtemp_c?.toFixed(0) === '-0'
                            ? '0'
                            : day.maxtemp_c?.toFixed(0)}
                          &deg;
                        </Typography>
                        <Divider orientation='vertical' />
                        <Typography
                          variant='body2'
                          sx={{ pl: '.2rem', pr: '.2rem' }}
                          color={grey[500]}
                        >
                          {day.mintemp_c?.toFixed(0) === '-0'
                            ? '0'
                            : day.mintemp_c?.toFixed(0)}
                          &deg;
                        </Typography>
                      </Grid>
                      <Grid
                        container
                        item
                        xs
                        justifyContent='center'
                        alignItems='center'
                      >
                        <img
                          height='15'
                          src='/raindrops-svgrepo-com.svg'
                          alt='raindrops'
                          style={{
                            filter:
                              'invert(52%) sepia(79%) saturate(2325%) hue-rotate(189deg) brightness(99%) contrast(104%)',
                          }}
                        />
                        <Typography variant='body2' sx={{ pl: '.1rem' }}>
                          {day.daily_chance_of_rain + '%'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('wind_kph')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='wind_kph'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('temp_c')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='temp_c'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('cloud_percent')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='cloud_percent'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('pressure_mb')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='pressure_mb'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('precip_mm')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='precip_mm'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
              <Grid
                item
                xs={chartWidth}
                onClick={() => setFullScreenChart('humidity')}
              >
                <WeatherChart
                  weatherData={forecast?.hourlyForecast}
                  measurement='humidity'
                  locationName=''
                  durationLabel=''
                  spanGaps
                />
              </Grid>
            </>
            <Grid sx={{ pt: '1rem' }} item container justifyContent='flex-end'>
              <Typography color={grey[500]} variant='body2'>
                {'Last updated: ' + lastUpdated}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions style={{ paddingTop: 0 }}>
        <Button
          onClick={() => {
            handleClose();
            setFullScreenChart('none');
          }}
        >
          Close
        </Button>
      </DialogActions>
      <ForecastFullscreenPopup
        handleClose={() => setFullScreenChart('none')}
        locationName={location?.name ? location.name : ''}
        forecast={forecast}
        lastUpdated={lastUpdated}
        fullScreenChart={fullScreenChart}
      />
    </Dialog>
  );
};

export default ForecastDetails;
